import { Component, OnInit } from '@angular/core';
import { TranslationService } from '../../common/services/languages/translation.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiHandlerService } from '../services/api-handler/api-handler.service';
import { MatDialog, MatSnackBar } from '@angular/material';
import { DialogBoxComponent } from '../../common/components/dialog-box/dialog-box.component';
import { AppState } from '../../store/state.model';
import { NgRedux, select } from '@angular-redux/store';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { FileDispatcher } from '../../store/file/file-dispatcher';
import { Observable } from 'rxjs';
import { Auth } from '../../store/auth/auth-state.model';
import { SpinnerService } from '../../common/components/spinner/overlay-spinner.service';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationBarComponent } from '../confirmation-bar/confirmation-bar.component';
import { ProductMappingControl } from './product-mapping.presenter';
import { NgIf, NgFor } from '@angular/common';
import { GenerationComponent } from '../generation/generation.component';
import { MatFormField, MatLabel, MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-product-mapping',
    templateUrl: './product-mapping.component.html',
    styleUrls: ['./product-mapping.component.scss'],
    imports: [NgIf, GenerationComponent, MatFormField, MatLabel, MatSelect, NgFor, MatOption, FormsModule, ReactiveFormsModule, MatInput, MatButton, TranslatePipe]
})
export class ProductMappingComponent implements OnInit {
  /**
 *@property From builder of the component
 **/
  public product: UntypedFormGroup;
  /**
   * @arg accessType the accessTypes avaiable of the record -submitted or approved
   */
  public accessType: Array<string>;
  /**
  *@arg authObservable to observe auth observable
  **/
  @select('auth') readonly authObservable: Observable<Auth>;
  /**
 *@arg selectedCapability to set the selected capabilities
 **/
  public selectedCapability: string;
  /**
 *@arg selectorCapabilities to store the capabilities which are received from the api
 **/
  public selectorCapabilities: any;
  /**
  * @arg productGeneration the generation value selected
  */
  public productGeneration: string;
  /**
* @arg isSelected stores false if capability list is empty
*/
  public isSelected: boolean;
  /**
   * @arg model returns the api body and endpoint
   */
  public model: any;
  constructor(
    public translate: TranslationService,
    public router: Router,
    public route: ActivatedRoute,
    public apiHandler: ApiHandlerService,
    public spinner: SpinnerService,
    public dialog: MatDialog,
    public ngRedux: NgRedux<AppState>,
    public commonDispatcher: CommonDispatcher,
    public fileDispatcher: FileDispatcher,
    private readonly snackBar: MatSnackBar,
    public fb: UntypedFormBuilder,
  ) {
    this.authObservable.subscribe((auth) => {
      this.accessType = auth.accessType;
    });
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit(): void {
    this.product = this.fb.group({
      productCode: [
        { value: '', disabled: false },
        [Validators.required,
        Validators.minLength(8),
        ]],
      productDescription: [
        { value: '', disabled: false },
        [Validators.required,
        Validators.minLength(8),
        ]]
    });
    this.defaults();
  }
  /**
   * @method defaults sets defaults
   */
  private defaults() {
    this.productGeneration = '';
    this.selectorCapabilities = [];
    this.fileDispatcher.setProductGeneration('');
    this.product.controls.productCode.reset();
    this.product.controls.productDescription.reset();
    this.selectedCapability = '';
    this.isSelected = false;
    this.model = new ProductMappingControl();
  }

  /**
   * @method showError displays the error message
   * @arg error error response from the api
   */
  private showError(error: object): void {
    if (error['errorCode'] === 'UNAUTHORIZED') {
      this.routeError();
    } else {
      this.commonDispatcher.setErrorCode(error['errorCode']);
      this.dialog.open(DialogBoxComponent, {
        data: {
          type: 'api-error',
          message: error['errorMessage']
        },
        width: '30em',
        height: 'auto'
      });
    }
  }
  /**
   * @arg routeError routes to error page
   * @returns void
   **/
  private routeError(): void {
    this.router.navigate(['dashboard/error'], { queryParams: {}, skipLocationChange: true });
  }
  /**
   * @method setGeneration should call the getCapabilities api if selcted generation
   * @arg generation sleceted generation
   */
  public setGeneration(generation: string): void {
    this.productGeneration = generation;
    if (generation) { this.getCapabilities(generation); }
  }
  /**
   * @method getProducts api call to get the capabilities for selected generation
   * @arg gen contains the selected generation
   */
  private getCapabilities(gen: string): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(this.model.getCapabilityApiEndpoint, this.model.getCapabilityApiType(gen)).subscribe({
      next: (response) => {
        this.successCapabilities(response);
        spinnerRef.close();
      },
      error: (error) => {
        this.showError(error);
        spinnerRef.close();
      }
    }
    );
  }
  /**
   *@method successCapabilities Sets the list of cpabilities for selected generation when called
   *@arg response with the list of capabilitis for selected generation
   **/
  public successCapabilities(response) {
    this.selectedCapability = '';
    this.selectorCapabilities = response.payload.capabilities;
  }
  /**
   *@method changeCapabilities Sets the isSelected to true when selected the capabilities
   **/
  public changeCapabilities(): void {
    this.isSelected = this.selectedCapability.length === 0 ? false : true;
  }
  /**
   *@method updateProductConfig will configure the product when called
   **/
  public updateProductConfig(): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.postRequest(this.model.getApiEndpoint, this.model.getApiRequest(this.product, this.selectedCapability), this.model.getApiType).subscribe({
      next: (response) => {
        this.openCommonSnack('updated', 'success');
        this.defaults();
        spinnerRef.close();
      },
      error: (error) => {
        this.showError(error);
        this.defaults();
        spinnerRef.close();
      }
    }
    );
  }

  /**
  * @method openCommonSnack will display noification bar based on data type
  * @arg dataType should contains updated when called
  * @arg classType should contains success when called
  */
  public openCommonSnack(dataType: string, classType: string): void {
    this.snackBar.openFromComponent(ConfirmationBarComponent, {
      data: {
        type: dataType
      },
      duration: 6000,
      verticalPosition: 'top',
      panelClass: ['custom-confirmation', classType],
    });
  }
}
